import { useState } from 'react'
import { useAllAccountSummary, useCreditAccounts } from 'const/syfApiQueries'
import setCookiesForMarketPlaceRedirect from 'helpers/setCookiesForMarketPlaceRedirect'
import isFeatureFlagEnabled from 'helpers/isFeatureFlagEnabled'
import isLoanAccount from 'helpers/isLoanAccount'
import hasPSCCAccounts from '../helpers/hasPSCCAccounts'
import areAllAccountsPermissible from '../helpers/areAllAccountsPermissible'
import handleLogout from '../helpers/handleLogout'
import config from '../const/config'

const useLogoutRedirect = () => {
  const client = localStorage.getItem('client')
  const [isLoggedIn, setIsLoggedIn] = useState(true)
  const { data: { accounts = [] } = {}, isError: isCreditAccountsAPIError } =
    useCreditAccounts()

  const allAccountSummaryResults = useAllAccountSummary(accounts)
  const isAnyAccountSummaryError = allAccountSummaryResults.some(
    ({ isError }) => isError
  )
  /*
   * Only successfully resolved summaries are included. Any account whose summary
   * failed to load will be absent here, causing areAllAccountsPermissible to
   * return false (undefined clientId = non-permissible) and blocking the redirect.
   */
  const allAccountSummaries = allAccountSummaryResults
    .filter(({ data, isSuccess }) => isSuccess && data)
    .map(({ data }) => data)

  if (
    accounts.length === 0 ||
    isCreditAccountsAPIError ||
    isAnyAccountSummaryError
  ) {
    return {
      isLoggedIn,
      isLogoutDisabled: !isLoggedIn,
      handleLogoutAndRedirect: () => {
        setIsLoggedIn(false)
        handleLogout()
      }
    }
  }

  return {
    isLoggedIn,
    isLogoutDisabled: !isLoggedIn,
    handleLogoutAndRedirect: (
      pageFunction: string = '',
      dataType: string = ''
    ) => {
      let logOffUrl
      let intcmpValue = ''
      setIsLoggedIn(false)

      const isRCLogoutToMarketplaceEnabled = isFeatureFlagEnabled(
        'isRCLogoutToMarketplaceEnabled'
      )

      /*
       * When isRCLogoutToMarketplaceEnabled is ON, and when there are no loans, it enables permissible RC clients along with universal.
       * When OFF, marketplace redirect only works if the user has all PSCC accounts and no loans.
       * ROK-6450: Loan users (Secured Installment, PayLater, SetPay) must NEVER be redirected to marketplace, regardless of flag state.
       */

      const allClientIds = allAccountSummaries.map(
        (summary) => summary?.product?.clientId
      )

      const hasLoans = allAccountSummaries.some((summary) =>
        isLoanAccount(summary?.product?.type)
      )

      const shouldRedirectToMarketplace =
        !hasLoans &&
        (isRCLogoutToMarketplaceEnabled
          ? areAllAccountsPermissible(allClientIds)
          : hasPSCCAccounts(accounts))

      if (shouldRedirectToMarketplace) {
        const pageFnc = pageFunction?.split(' ').join('')
        /*
         * clientId for intcmp is fetched from accountSummary.
         * For RC, this will be the client name; for PSCC, it will be 'paysol'.
         */
        const clientForIntcmp = allAccountSummaries[0]?.product?.clientId
        /*
         * ROK-5497: Use client = universal | {clientId} in localstorage in cookies (vsc),
         * but always use the actual lead clientId in the intcmp value.
         */
        /**
         * intcmp value should look like SiteIdentifier_ClientName_pgfunc_data-type_internaldomain_int
         * intcmp always uses the actual lead account clientId (never 'universal')
         */
        const intcmpIndividualAttributes = [
          'vista',
          clientForIntcmp,
          pageFnc,
          dataType,
          'synchrony',
          'int'
        ]
        /*
         * Example:
         * - RC flag ON + permissible accounts -> cookie uses localStorage client
         *   (for example, 'universal' or an RC client), while intcmp uses lead clientId.
         * - RC flag OFF + hasPSCCAccounts(accounts) true -> all accounts are PSCC,
         *   so lead clientId resolves to 'paysol' and both cookie/intcmp use 'paysol'.
         */
        const cookieClientName = isRCLogoutToMarketplaceEnabled
          ? client
          : clientForIntcmp
        setCookiesForMarketPlaceRedirect(cookieClientName)
        logOffUrl = config.MARKETPLACE_URL

        // Get the filter list, where the attributes are not empty
        const filteredIntcmpIndividualAttributes =
          intcmpIndividualAttributes.filter(
            (intcmpAttribute) =>
              intcmpAttribute && intcmpAttribute.trim().length > 0
          )
        intcmpValue = filteredIntcmpIndividualAttributes.join('_')
      }
      handleLogout(logOffUrl, intcmpValue)
    }
  }
}

export default useLogoutRedirect
